import { api } from '../../lib/api';
import type {
  ArticleListResponse,
  ArticleDetail,
  ArticleStatus,
  ArticleCategory,
  ArticleCluster,
  CreateArticleInput,
  UpdateArticleInput,
  AddReferenceInput,
  ArticleReference,
} from './types';

interface ListParams {
  status?: ArticleStatus;
  cluster?: ArticleCluster;
  category?: ArticleCategory;
  q?: string;
  page?: number;
  limit?: number;
}

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    usp.set(key, String(value));
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export function fetchArticles(params: ListParams = {}): Promise<ArticleListResponse> {
  return api.get<ArticleListResponse>(`/admin/articles${buildQuery({ ...params })}`);
}

export function fetchArticle(id: string): Promise<ArticleDetail> {
  return api.get<ArticleDetail>(`/admin/articles/${id}`);
}

export function createArticle(input: CreateArticleInput): Promise<ArticleDetail> {
  return api.post<ArticleDetail>('/admin/articles', input);
}

export function updateArticle(
  id: string,
  input: UpdateArticleInput,
  options: { autosave?: boolean } = {},
): Promise<ArticleDetail> {
  const suffix = options.autosave ? '?autosave=1' : '';
  return api.patch<ArticleDetail>(`/admin/articles/${id}${suffix}`, input);
}

export function deleteArticle(id: string): Promise<void> {
  return api.delete(`/admin/articles/${id}`);
}

export function publishArticle(id: string): Promise<ArticleDetail> {
  return api.post<ArticleDetail>(`/admin/articles/${id}/publish`, {});
}

export function unpublishArticle(id: string): Promise<ArticleDetail> {
  return api.post<ArticleDetail>(`/admin/articles/${id}/unpublish`, {});
}

export function sendForReview(id: string): Promise<ArticleDetail> {
  return api.post<ArticleDetail>(`/admin/articles/${id}/review`, {});
}

/** Termin-Freigabe. `scheduledFor` als ISO-String, muss in der Zukunft liegen. */
export function scheduleArticle(id: string, scheduledFor: string): Promise<ArticleDetail> {
  return api.post<ArticleDetail>(`/admin/articles/${id}/schedule`, { scheduledFor });
}

export function unscheduleArticle(id: string): Promise<ArticleDetail> {
  return api.post<ArticleDetail>(`/admin/articles/${id}/unschedule`, {});
}

export function approveArticle(
  id: string,
  body: { reviewerNote?: string } = {},
): Promise<ArticleDetail> {
  return api.post<ArticleDetail>(`/admin/articles/${id}/approve`, body);
}

export function addReference(id: string, input: AddReferenceInput): Promise<ArticleReference> {
  return api.post<ArticleReference>(`/admin/articles/${id}/references`, input);
}

export function updateReference(
  refId: string,
  input: Partial<AddReferenceInput>,
): Promise<ArticleReference> {
  return api.patch<ArticleReference>(`/admin/articles/references/${refId}`, input);
}

export function deleteReference(refId: string): Promise<void> {
  return api.delete(`/admin/articles/references/${refId}`);
}

export interface ImageUploadResult {
  url: string;
  key: string;
  size: number;
  mimeType: string;
  alt: string | null;
  markdown: string;
}

export async function uploadArticleImage(
  articleId: string,
  file: File,
  alt: string,
): Promise<ImageUploadResult> {
  const base64 = await fileToBase64(file);
  return api.post<ImageUploadResult>(`/admin/articles/${articleId}/images`, {
    filename: file.name,
    mimeType: file.type,
    base64,
    alt,
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}
